import { useState, useRef, useCallback, createElement } from 'react';
import { api } from '../api/client';
import { ROUTES } from '../api/routes';
import { useAuthStore } from '../store/authStore';
import { saveAccountToLocal } from '../auth/savedAccounts';
import { toast } from 'sonner';
import { AvatarCropper } from '../components/ui/AvatarCropper';

export interface UseAvatarUploadOptions {
  /** ID do usuário alvo (default: user.id autenticado) */
  targetUserId?: string;
  /** Executado após upload com sucesso */
  onSuccess?: (fotoUrl: string) => void;
  /** Executado em caso de falha */
  onError?: (error: Error) => void;
  /** Tamanho final em px do recorte (default: 200) */
  outputSize?: number;
}

/**
 * Redimensiona e comprime uma imagem client-side via HTML5 Canvas.
 * Produz uma imagem limpa em JPEG 400x400 max (~80-150KB).
 */
export async function compressImage(file: File, maxDimension = 400, quality = 0.85): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      return reject(new Error('O arquivo selecionado não é uma imagem válida.'));
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Erro ao ler o arquivo de imagem.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Erro ao carregar a imagem para processamento.'));
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxDimension) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return resolve(reader.result as string);
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export function useAvatarUpload(options?: UseAvatarUploadOptions) {
  const [isUploading, setIsUploading] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const authUser = useAuthStore((s) => s.user);
  const updateUserAvatar = useAuthStore((s) => s.updateUserAvatar);

  const targetId = options?.targetUserId || authUser?.id;
  const outputSize = options?.outputSize || 200;

  const uploadCroppedBase64 = useCallback(
    async (base64: string) => {
      setCropFile(null);
      if (!base64) return null;
      if (!targetId) {
        toast.error('Identificador de usuário não encontrado.');
        return null;
      }

      setIsUploading(true);
      try {
        let finalUrl = base64;
        try {
          const { data } = await api.post<{ success: boolean; url: string }>(
            ROUTES.upload.fotoUsuario(targetId),
            { fotoBase64: base64 },
          );
          if (data?.url) {
            finalUrl = data.url;
          }
        } catch (uploadErr) {
          // Fallback via PATCH /auth/me se for o próprio usuário logado
          if (targetId === authUser?.id) {
            const { data } = await api.patch(ROUTES.auth.me, { fotoUrl: base64 });
            if (data?.fotoUrl) {
              finalUrl = data.fotoUrl;
            }
          } else {
            throw uploadErr;
          }
        }

        // Atualizar store global se for o usuário logado
        if (targetId === authUser?.id) {
          updateUserAvatar(finalUrl);

          // Sincronizar contas salvas no localStorage
          try {
            saveAccountToLocal({
              id: authUser.id,
              nome: authUser.nome,
              email: authUser.email,
              avatar: finalUrl,
              perfil: authUser.perfil,
              role: authUser.role,
              cargo: authUser.cargo,
              nomeEscola: authUser.nomeEscola,
              hasBiometria: !!authUser.biometriaAtiva,
              hasSenha: !!authUser.hasSenha,
            });
          } catch {}
        }

        toast.success('Foto de perfil atualizada com sucesso!');
        if (options?.onSuccess) {
          options.onSuccess(finalUrl);
        }
        return finalUrl;
      } catch (err: any) {
        console.error('[useAvatarUpload] Erro:', err);
        const msg = err.response?.data?.message || err.message || 'Falha ao enviar a foto de perfil.';
        toast.error(msg);
        if (options?.onError) {
          options.onError(err);
        }
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [targetId, authUser, updateUserAvatar, options],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      if (!file) return null;
      // Ao invés de subir direto sem recorte, aciona o fluxo de corte
      if (file.type.startsWith('image/')) {
        setCropFile(file);
        return null;
      }
      toast.error('Selecione um arquivo de imagem válido.');
      return null;
    },
    [],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          toast.error('Selecione um arquivo de imagem válido.');
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error('A imagem selecionada é muito grande (máximo 10MB).');
          return;
        }
        setCropFile(file);
      }
      e.target.value = '';
    },
    [],
  );

  const openFilePicker = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleCropCancel = useCallback(() => {
    setCropFile(null);
  }, []);

  const cropperModal = cropFile ? createElement(AvatarCropper, {
    file: cropFile,
    onCropped: uploadCroppedBase64,
    onCancel: handleCropCancel,
    outputSize,
  }) : null;

  return {
    isUploading,
    uploadFile,
    uploadCroppedBase64,
    openFilePicker,
    fileInputRef,
    handleInputChange,
    cropFile,
    setCropFile,
    cropperModal,
    handleCropCancel,
  };
}
